import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Wallet, X, Loader2, Check, Gift } from "lucide-react";

export const PriceModal = ({ open, contract, index, total, onSaved, onClose }) => {
  const [amount, setAmount] = useState("");
  const [period, setPeriod] = useState("month");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setAmount("");
      setPeriod(contract?.source === "card" ? "year" : "month");
    }
  }, [open, contract?.id, contract?.source]);

  if (!open || !contract) return null;
  const isCard = contract.source === "card";

  const save = async () => {
    const val = parseFloat(String(amount).replace(",", "."));
    if (isNaN(val) || val < 0) {
      toast.error("Entrez un montant valide");
      return;
    }
    const monthly = period === "year" ? Math.round((val / 12) * 100) / 100 : Math.round(val * 100) / 100;
    setSaving(true);
    try {
      const { data } = await api.patch(`/contracts/${contract.id}`, { monthly_cost: monthly });
      toast.success(`Montant enregistré : ${monthly} €/mois`);
      onSaved?.(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const saveZero = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/contracts/${contract.id}`, { monthly_cost: 0 });
      toast.success(isCard ? "Carte gratuite enregistrée" : "Enregistré : gratuit");
      onSaved?.(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[85] flex items-end sm:items-center justify-center p-3 sm:p-6" data-testid="price-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !saving && onClose()} />
      <div className="relative w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl animate-in-up">
        <button
          onClick={() => !saving && onClose()}
          className="absolute top-4 right-4 w-8 h-8 grid place-items-center rounded-lg text-muted-foreground hover:bg-muted transition"
          aria-label="Fermer"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary grid place-items-center">
          <Wallet className="w-5 h-5" />
        </div>
        {total > 1 && (
          <p data-testid="price-progress" className="text-xs font-mono uppercase tracking-wide text-muted-foreground mt-4">Élément {index} / {total}</p>
        )}
        <h2 className={`font-heading text-xl font-bold tracking-tight ${total > 1 ? "mt-1" : "mt-4"}`}>{isCard ? "Quelle est la cotisation de cette carte ?" : "Quel montant payez-vous ?"}</h2>
        <p className="text-sm text-muted-foreground mt-1">
          {isCard ? (
            <>Indiquez la cotisation annuelle de <span className="font-medium text-foreground">{contract.name}</span> (0 si gratuite) pour qu'elle compte dans votre score et vos économies.</>
          ) : (
            <>Aucun prix détecté pour <span className="font-medium text-foreground">{contract.name}</span>. Indiquez-le pour un calcul d'économies précis.</>
          )}
        </p>

        <div className="mt-5 flex items-center gap-2">
          <div className="relative flex-1">
            <input
              data-testid="price-input"
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              autoFocus
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") save(); }}
              placeholder="0,00"
              className="w-full h-11 pl-4 pr-9 rounded-xl border border-input bg-background text-sm outline-none focus:ring-2 focus:ring-ring/40"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">€</span>
          </div>
          <div className="flex rounded-xl border border-border overflow-hidden shrink-0">
            <button
              data-testid="price-period-month"
              onClick={() => setPeriod("month")}
              className={`h-11 px-3 text-sm font-medium transition ${period === "month" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
            >
              / mois
            </button>
            <button
              data-testid="price-period-year"
              onClick={() => setPeriod("year")}
              className={`h-11 px-3 text-sm font-medium transition border-l border-border ${period === "year" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
            >
              / an
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 mt-5">
          <button
            data-testid="price-save-btn"
            onClick={save}
            disabled={saving}
            className="flex-1 h-11 rounded-xl bg-primary text-primary-foreground font-medium text-sm hover:opacity-90 disabled:opacity-60 transition flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Enregistrer
          </button>
          <button
            data-testid="price-skip-btn"
            onClick={() => !saving && onClose()}
            className="h-11 px-4 rounded-xl border border-border text-sm font-medium text-muted-foreground hover:bg-muted transition"
          >
            {isCard ? "Je ne sais pas" : "Gratuit / je ne sais pas"}
          </button>
        </div>

        {isCard && (
          <button
            data-testid="price-free-card-btn"
            onClick={saveZero}
            disabled={saving}
            className="mt-2 w-full h-10 rounded-xl border border-emerald-500/50 text-emerald-700 dark:text-emerald-300 text-sm font-medium hover:bg-emerald-500/10 disabled:opacity-60 transition flex items-center justify-center gap-2"
          >
            <Gift className="w-4 h-4" /> Cette carte est gratuite (0 €)
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default PriceModal;
